// src/Hero.js

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Flip } from 'gsap/Flip';
import { useAuth } from '../context/AuthContext';
import { validateCustomer } from '../services/customerAuth';

gsap.registerPlugin(ScrollTrigger, Flip);

function Hero() {
  const ctaButtonRef = useRef(null);
  const placeholderRef = useRef(null);
  const [isFloating, setIsFloating] = useState(false);
  const pulseTweenRef = useRef(null);
  const shineTweenRef = useRef(null);

  // Login State
  const [customerCode, setCustomerCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!customerCode.trim()) {
      setError('고객 코드를 입력해주세요.');
      return;
    }

    setIsLoading(true);

    try {
      const customerData = await validateCustomer(customerCode.trim());
      login(customerData);
      navigate(`/gallery/${customerData.id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const playAnims = () => {
    pulseTweenRef.current?.play();
    shineTweenRef.current?.play();
  };
  const pauseAnims = () => {
    pulseTweenRef.current?.pause();
    shineTweenRef.current?.pause();
  };

  // Effect for ScrollTrigger & Base Animations
  useEffect(() => {
    const button = ctaButtonRef.current;
    let st = null;
    const pulseTween = pulseTweenRef.current;
    const shineTween = shineTweenRef.current;

    if (button) {
      // pulseTweenRef.current = gsap.to(button, { scale: 1.07, duration: 0.9, ease: 'sine.inOut', repeat: -1, yoyo: true, paused: true });
      gsap.set(button, { '--shine-pos-x': '-110%' });
      shineTweenRef.current = gsap.to(button, { '--shine-pos-x': '210%', duration: 0.9, ease: 'power1.inOut', repeat: -1, repeatDelay: 1, delay: 1, paused: true });

      st = ScrollTrigger.create({
        trigger: document.body,
        start: 1,
        end: "max",
        onEnter: () => setIsFloating(true),
        onLeaveBack: () => setIsFloating(false),
        onUpdate: () => {
          if (gsap.getProperty(button, "opacity") > 0) playAnims();
          else pauseAnims();
        }
      });

      if (window.scrollY > 0) {
        setIsFloating(true);
        button.classList.add('is-actually-floating');
        playAnims();
      } else {
        setIsFloating(false);
        button.classList.remove('is-actually-floating');
        playAnims();
      }
    }

    return () => {
      if (st) st.kill();
      pulseTween?.kill();
      shineTween?.kill();
      gsap.killTweensOf(button);
    };
  }, []);

  // Effect for Flip Animation
  useEffect(() => {
    const button = ctaButtonRef.current;
    if (!button) return;

    const state = Flip.getState(button, { props: "border-radius" });
    button.classList.toggle('is-actually-floating', isFloating);

    const shouldAnimate = (isFloating && window.scrollY > 0) || (!isFloating && window.scrollY === 0);
    const isInitialLoad = !Flip.isFlipping(button);

    if (shouldAnimate || !isInitialLoad) {
      Flip.from(state, {
        // --- MODIFIED Duration & Ease ---
        duration: 1.2, // Slower duration
        ease: "elastic.inOut(0.2, 0.9)", // More amplitude, slower period
        // ---------------------------------
        onStart: playAnims,
        onComplete: playAnims,
      });
    }

  }, [isFloating]);

  // --- Component Render ---
  return (
    <section className="hero" id="home">
      <div className="container">
        <div className="hero-text">
          <p>윈도우 클리닝, 외벽 청소부터 건물 정기 관리까지.<br /> 전문 연구소의 섬세한 케어로 여러분의 공간에 맑은 뷰와 가치를 더해드립니다.</p>
          <div className="hero-button-container" ref={placeholderRef}>
            <button
              className="cta-button"
              ref={ctaButtonRef}
              onClick={(e) => {
                e.preventDefault();
                const contactSection = document.getElementById('contact');
                if (contactSection) {
                  contactSection.scrollIntoView({ behavior: 'smooth' });
                }
              }}
            >
              무료 견적
            </button>
          </div>

          {/* Hero Login Form */}
          <div className="hero-login-container" style={{ marginTop: '40px', maxWidth: '400px', textAlign: 'left' }}>
            <h2 style={{ fontSize: '1.8rem', marginTop: '0', marginBottom: '20px' }}>고객 사진 확인</h2>
            <form onSubmit={handleLoginSubmit} style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>
              <input
                type="text"
                value={customerCode}
                onChange={(e) => setCustomerCode(e.target.value)}
                placeholder="고객 코드 (예: CUST001)"
                disabled={isLoading}
                style={{
                  flex: 1,
                  padding: '10px',
                  borderRadius: '5px',
                  border: '1px solid #ccc',
                  fontSize: '0.9rem'
                }}
              />
              <button
                type="submit"
                disabled={isLoading}
                className="cta-button"
                style={{
                  padding: '10px 20px',
                  fontSize: '0.9rem',
                  borderRadius: '5px',
                  minWidth: '80px'
                }}
              >
                {isLoading ? '...' : '확인'}
              </button>
            </form>
            {error && <p style={{ color: '#d32f2f', fontSize: '0.85rem', marginTop: '5px' }}>{error}</p>}
          </div>

        </div>
        <div className="hero-image">
          <img src="img/mainhero.png" alt="맑은뷰연구소 메인 이미지" />
        </div>
      </div>
    </section>
  );
}

export default Hero;